import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Individual investor profile state
 * Stores personal, employment, and investment information for individual investors
 */
export interface ProfState {
  // Contact information
  phoneNumber: string | null;
  address: string | null;
  postalCode: string | null;

  // Industry and occupation
  industryGroup: string | null;
  industryDropdownValues: any | null; // TODO: Define proper type
  industry: string | null;
  industryOthers: string | null;
  occupation: string | null;
  experience: string | null;

  // Employment details
  employmentStatus: string | null;
  employmentPosition: string | null;
  employerName: string | null;
  numberofYears: number | null; // Years of experience (note: lowercase 'of' for backward compatibility)

  // Personal information
  nationality: string | null;
  gender: string | null;
  dateOfBirth: string | null; // ISO date string
  countryOfBirth: string | null;
  countryOfResidence: string | null;
  region: string | null;
  city: string | null;

  // Investment details
  investmentAmount: number | null;
  investorDeclarationQualified: boolean | null;
  isPep: boolean | null; // Politically Exposed Person

  // Tax residency
  numberOfTaxResidenceCountries: number | null;
  taxResidenceCountries: string[];

  // Referral
  referralSource: string | null;
  otherReferral: string | null;
}

const initialState: ProfState = {
  phoneNumber: null,
  industryGroup: null,
  industryDropdownValues: null,
  industry: null,
  industryOthers: null,
  occupation: null,
  experience: null,
  nationality: null,
  gender: null,
  employmentStatus: null,
  employmentPosition: null,
  dateOfBirth: null,
  countryOfBirth: null,
  countryOfResidence: null,
  region: null,
  city: null,
  address: null,
  postalCode: null,
  investmentAmount: null,
  investorDeclarationQualified: null,
  isPep: null,
  employerName: null,
  referralSource: null,
  otherReferral: null,
  numberofYears: null,
  numberOfTaxResidenceCountries: null,
  taxResidenceCountries: [],
};

/**
 * Generic reducer factory for simple field updates
 * Eliminates duplication for setters that just assign a value
 */
const createFieldSetter = <K extends keyof ProfState>(field: K) => {
  return (state: ProfState, action: PayloadAction<ProfState[K]>) => {
    state[field] = action.payload;
  };
};

const profSlice = createSlice({
  name: "prof",
  initialState,
  reducers: {
    // Contact information
    setPhoneRedux: createFieldSetter("phoneNumber"),
    setAddressRedux: createFieldSetter("address"),
    setPostalCodeRedux: createFieldSetter("postalCode"),

    // Industry and occupation
    setIndustryGroupRedux: createFieldSetter("industryGroup"),
    setIndustryDropdownValuesRedux: createFieldSetter("industryDropdownValues"),
    setIndustryRedux: createFieldSetter("industry"),
    setIndustryOthersRedux: createFieldSetter("industryOthers"),
    setOccupationeRedux: createFieldSetter("occupation"),
    setExperienceRedux: createFieldSetter("experience"),

    // Employment details
    setEmploymentStatusRedux: createFieldSetter("employmentStatus"),
    setEmploymentPositionRedux: createFieldSetter("employmentPosition"),
    setEmployerNameRedux: createFieldSetter("employerName"),
    setNumberOfYearsRedux: createFieldSetter("numberofYears"),

    // Personal information
    setNationalityRedux: createFieldSetter("nationality"),
    setGenderRedux: createFieldSetter("gender"),
    setdateOfBirthRedux: createFieldSetter("dateOfBirth"),
    setCountryOfBirthRedux: createFieldSetter("countryOfBirth"),
    setCountryOfResidenceRedux: createFieldSetter("countryOfResidence"),
    setRegionRedux: createFieldSetter("region"),
    setCityRedux: createFieldSetter("city"),

    // Investment details
    setInvestmentAmountRedux: createFieldSetter("investmentAmount"),
    setInvestorDeclarationQualifiedRedux: createFieldSetter("investorDeclarationQualified"),
    setIsPepRedux: createFieldSetter("isPep"),

    // Tax residency
    setNumberOfTaxResidenceCountries: createFieldSetter("numberOfTaxResidenceCountries"),
    setTaxResidenceCountries: createFieldSetter("taxResidenceCountries"),

    // Referral
    setReferralSourceRedux: createFieldSetter("referralSource"),
    setOtherReferralRedux: createFieldSetter("otherReferral"),

    /**
     * Bulk update multiple fields at once
     * Useful for hydrating state from API response
     */
    hydrateProfState: (state, action: PayloadAction<Partial<ProfState>>) => {
      return { ...state, ...action.payload };
    },

    /**
     * Reset to initial state
     */
    resetProfState: () => initialState,
  },
});

export const {
  setIndustryGroupRedux,
  setIndustryDropdownValuesRedux,
  setIndustryRedux,
  setOccupationeRedux,
  setExperienceRedux,
  setPhoneRedux,
  setNationalityRedux,
  setGenderRedux,
  setEmploymentStatusRedux,
  setEmploymentPositionRedux,
  setdateOfBirthRedux,
  setCountryOfBirthRedux,
  setCountryOfResidenceRedux,
  setRegionRedux,
  setCityRedux,
  setAddressRedux,
  setPostalCodeRedux,
  setIndustryOthersRedux,
  setInvestmentAmountRedux,
  setInvestorDeclarationQualifiedRedux,
  setIsPepRedux,
  setEmployerNameRedux,
  setReferralSourceRedux,
  setOtherReferralRedux,
  setNumberOfYearsRedux,
  setNumberOfTaxResidenceCountries,
  setTaxResidenceCountries,
  hydrateProfState,
  resetProfState,
} = profSlice.actions;

export default profSlice.reducer;
